// NavRail.tsx — Atrium tier-1 icon rail (Sprint 82, ADR-082).
// ----------------------------------------------------------------------------
// 84px rail: CondoPro logo tile, stacked icon+label section items for the
// active role, settings/system pinned to the bottom, avatar at the very bottom.
// Visual spec mirrors prototype `.rail` (theme2.jsx ~lines 20-31, nav2.jsx
// NavRail). Brand stays CondoPro — the logo tile shows "C".
import React from 'react';
import type { AtriumSection } from './navModel';
import type { MenuGroupKey } from '../dashboard/menuConfig';

export interface NavRailProps {
  sections:     AtriumSection[];
  activeKey:    MenuGroupKey | undefined;
  onSection:    (key: MenuGroupKey) => void;
  avatar:       string;
}

const railStyle: React.CSSProperties = {
  width: 'var(--rail)', flex: 'none', background: 'var(--card)',
  borderRight: '1px solid var(--line)', display: 'flex',
  flexDirection: 'column', alignItems: 'center', padding: '16px 0', gap: 6,
};

const logoStyle: React.CSSProperties = {
  width: 42, height: 42, borderRadius: 12, background: 'var(--blue)',
  color: '#fff', display: 'flex', alignItems: 'center',
  justifyContent: 'center', fontWeight: 800, fontSize: 20,
  marginBottom: 12, boxShadow: 'var(--shc)',
};

function railItemStyle(active: boolean): React.CSSProperties {
  return {
    width: 64, padding: '9px 0 7px', borderRadius: 13, display: 'flex',
    flexDirection: 'column', alignItems: 'center', gap: 5, cursor: 'pointer',
    color: active ? 'var(--blue)' : 'var(--mut)',
    background: active ? 'var(--blue-s)' : 'transparent',
    position: 'relative', transition: 'background .12s,color .12s',
    fontSize: 18, border: 0, fontFamily: 'inherit',
  };
}

const labelStyle: React.CSSProperties = {
  fontSize: 10.5, fontWeight: 700, letterSpacing: '-.1px', lineHeight: 1.1,
  textAlign: 'center',
};

const avatarStyle: React.CSSProperties = {
  width: 38, height: 38, borderRadius: '50%', background: 'var(--blue-s)',
  color: 'var(--blue)', display: 'flex', alignItems: 'center',
  justifyContent: 'center', fontWeight: 800, fontSize: 13,
};

function RailItem({ section, active, onClick }: {
  section: AtriumSection; active: boolean; onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-current={active ? 'page' : undefined}
      title={section.label}
      style={railItemStyle(active)}
      onClick={onClick}
    >
      {/* active left bar (prototype .ritem.on:before) */}
      {active && (
        <span aria-hidden="true" style={{
          content: '""', position: 'absolute', left: -10, top: 12, bottom: 12,
          width: 3.5, borderRadius: '0 4px 4px 0', background: 'var(--blue)',
        }} />
      )}
      <span aria-hidden="true" style={{ display: 'flex' }}>{section.icon}</span>
      <span style={labelStyle}>{section.label}</span>
    </button>
  );
}

export const NavRail: React.FC<NavRailProps> = ({
  sections, activeKey, onSection, avatar,
}) => {
  const top = sections.filter(s => !s.bottom);
  const bottom = sections.filter(s => s.bottom);

  return (
    <nav style={railStyle} aria-label="Primary">
      {/* Brand = CondoPro (NOT Atrium) */}
      <div style={logoStyle} aria-label="CondoPro">C</div>

      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        gap: 4, width: '100%', flex: 1,
      }}>
        {top.map(s => (
          <RailItem
            key={s.key}
            section={s}
            active={activeKey === s.key}
            onClick={() => onSection(s.key)}
          />
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
        {bottom.map(s => (
          <RailItem
            key={s.key}
            section={s}
            active={activeKey === s.key}
            onClick={() => onSection(s.key)}
          />
        ))}
        {/* Sprint 144 (R14-6/ADR-139) — the gear used to open the Account
            Settings modal, whose every control (notification toggles, dark
            mode, language, timezone, 2FA, "Active Sessions") was client-only
            with no endpoint behind it. The modal is gone, so the shortcut that
            opened it is gone too rather than pointing at nothing. */}
        <div style={avatarStyle} aria-hidden="true">{avatar}</div>
      </div>
    </nav>
  );
};
